// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import UAT from 'common/user-activity-tracking';
import { isNetworkError } from 'helpers/isNetworkError';
import addUrlParams from './addUrlParams';
import emptyFn from './emptyFn';

const RETRY_DELAY = 500;
const GET_REQUEST_RETRY_COUNT = 3;

type RequestConfig = RequestInit & {
    headers?: Record<string, string>;
};

declare global {
    interface Window {
        Ajax?: { activeRequestCount: number };
    }
}

const request = (url: string, config: RequestConfig = {}, retryCount = 0): Promise<unknown> => {
    if (window.Ajax) {
        window.Ajax.activeRequestCount++;
    }

    const headers: Record<string, string> = {
        'X-Requested-With': 'XMLHttpRequest',
    };
    const tokenEl = document.getElementById('forgery_protection_token') as HTMLMetaElement | null;
    if (tokenEl) {
        headers['X-Forgery-Protection-Token'] = tokenEl.content;
    }

    config = {
        credentials: 'same-origin',
        ...config,
        headers: {
            ...headers,
            ...(config.headers || {}),
        },
    };

    return fetch(url, config)
        .then(response => {
            if (response.status === 400) {
                window.location.reload();
                return new Promise(emptyFn);
            }
            if (!response.ok) {
                if (response.headers.get('Content-Type')?.startsWith('application/json')) {
                    return response.json()
                        .then(data => Promise.reject(new Error(data.message ?? JSON.stringify(data))));
                }
                return response.text()
                    .then(body => Promise.reject(new Error(body)));
            }
            return response.json();
        })
        .catch(error => {
            if (error instanceof TypeError && isNetworkError(error)) {
                if (retryCount > 0) {
                    return new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
                        .then(() => request(url, config, --retryCount));
                }
                throw error;
            }
            throw error;
        })
        .finally(() => {
            if (window.Ajax) {
                window.Ajax.activeRequestCount--;
            }
        });
};

export default {
    get: (url: string, params: Record<string, unknown> = {}) => request(addUrlParams(url, params), { method: 'GET' }, GET_REQUEST_RETRY_COUNT),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post: (url: string, params: Record<string, any> = {}) => {
        UAT.dispatchAction('REQUEST', { url });
        const headers: Record<string, string> = {};
        let body: BodyInit;
        if (params instanceof FormData) {
            body = params;
        } else {
            body = JSON.stringify(params);
            headers['Content-Type'] = 'application/json; charset=utf-8';
        }
        return request(url, {
            method: 'POST',
            headers,
            body,
        });
    },
};
